/**
 * SettingsMenuPage — the `/settings` landing (T12.2). The ☰ drawer is the
 * primary entry into each surface, but the route still needs a landing for
 * direct navigation / refresh / the desktop "Settings" home. It mirrors the
 * drawer's IA: one flat ordered list (T27.2.7.6 / MN1 dropped the former
 * Household/App grouping here too), each entry routing to its surface, with the
 * shared `Icon` set instead of emoji. On desktop the SettingsLayover sidebar
 * carries the same nav, so this landing is mostly the phone refresh target.
 */

import type { CSSProperties } from 'react';
import { NavLink } from 'react-router-dom';
import { useMe } from '@shopista/shared/api/hooks';
import { Icon } from '../../components';
import { SETTINGS_MENU } from './settingsMenu';
import { usePublishLayoverTitle } from '../../stores/useLayoverTitle';

const pageStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-4)',
};

const idStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  paddingBottom: 'var(--space-3)',
  borderBottomWidth: '0.0625rem',
  borderBottomStyle: 'solid',
  borderBottomColor: 'var(--color-border-subtle)',
};

const avatarStyle: CSSProperties = {
  width: '2.625rem',
  height: '2.625rem',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'var(--color-bg-muted)',
  display: 'grid',
  placeItems: 'center',
  fontSize: 'var(--font-size-lg)',
  flex: '0 0 auto',
};

const idNameStyle: CSSProperties = {
  fontWeight: 'var(--font-weight-bold)',
  fontSize: 'var(--font-size-base)',
};

const idSubStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  marginTop: 'var(--space-1)',
};

const menuItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  textDecoration: 'none',
  color: 'var(--color-text-primary)',
  minHeight: '2.75rem',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
  marginBottom: 'var(--space-2)',
};

const menuIconStyle: CSSProperties = {
  fontSize: 'var(--font-size-lg)',
  flex: '0 0 auto',
};

const menuLabelStyle: CSSProperties = {
  fontWeight: 'var(--font-weight-semibold)',
  fontSize: 'var(--font-size-sm)',
};

const menuDescStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  marginTop: 'var(--space-1)',
};

export function SettingsMenuPage(): JSX.Element {
  const meQuery = useMe();
  const user = meQuery.data;

  // The `/settings` landing has no SettingsLayover; publish its own name so the
  // app header shows "Settings" on the menu home (the landing's real title).
  usePublishLayoverTitle('Settings');

  return (
    <div style={pageStyle} data-testid="settings-page">
      <div style={idStyle}>
        <span style={avatarStyle} aria-hidden="true">
          <Icon name="home" data-testid="settings-menu-avatar-icon" />
        </span>
        <div>
          <div style={idNameStyle} data-testid="settings-menu-household">
            {user?.householdName ?? 'Your household'}
          </div>
          {user?.email && <div style={idSubStyle}>{user.email}</div>}
        </div>
      </div>

      {SETTINGS_MENU.map((entry) => {
        const testId = `settings-menu-${entry.key}`;
        return (
        <NavLink
          key={entry.to}
          to={entry.to}
          style={menuItemStyle}
          data-testid={testId}
        >
          <span style={menuIconStyle} aria-hidden="true">
            <Icon name={entry.icon} data-testid={`${testId}-icon`} />
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={menuLabelStyle}>{entry.label}</span>
            <span style={menuDescStyle}>{entry.desc}</span>
          </span>
          <span aria-hidden="true" style={{ color: 'var(--color-text-muted)' }}>
            <Icon name="chevron-right" />
          </span>
        </NavLink>
        );
      })}
    </div>
  );
}
